import React, { useState } from "react";
import { useAppSelector } from "../../redux/store";
import { currentUserSelector } from "../../redux/reducers/userSlice";

export default function ImuWidget() {
  const [inputValue, setInputValue] = useState("");
  const [aliquotaAttuale, setAliquotaAttuale] = useState(1.06);
  const [aliquotaConcordato, setAliquotaConcordato] = useState(0.4);
  const [abbattimento, setAbbattimento] = useState(75);
  const [risultato, setRisultato] = useState<any>(null);
  const [errore, setErrore] = useState("");
  const currentUser = useAppSelector(currentUserSelector);
  const isAdministrator = currentUser?.authorityChecks.isAdministrator;
  function parseCurrencyToNumber(str: any) {
    if (typeof str !== "string") return NaN;
    str = str.trim().replace(/[€\s]/g, "");
    const lastComma = str.lastIndexOf(",");
    const lastDot = str.lastIndexOf(".");
    if (lastComma > -1 && lastDot > -1) {
      const decPos = Math.max(lastComma, lastDot);
      const decSep = str[decPos];
      const thousandsSep = decSep === "," ? "." : ",";
      str = str.replace(new RegExp("\\" + thousandsSep, "g"), "");
      str = str.replace(decSep, ".");
    } else if (lastComma > -1) {
      str = str.replace(/\./g, "");
      str = str.replace(",", ".");
    } else {
      const parts = str.split(".");
      if (parts.length > 2) {
        const dec = parts.pop();
        str = parts.join("") + "." + dec;
      }
    }
    return Number(str);
  }
  function formatEUR(value: any) {
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(value);
  }
  function getFattore() {
    const num = (abbattimento / 100) * (aliquotaConcordato / 100);
    const den = aliquotaAttuale / 100;
    return den === 0 ? 0 : num / den;
  }
  function calcola() {
    setErrore("");
    const imuAttuale = parseCurrencyToNumber(inputValue);
    if (!isFinite(imuAttuale) || imuAttuale <= 0) {
      setErrore("Inserisci un importo IMU annuale valido (es. 1.780,80).");
      setRisultato(null);
      return;
    }
    const fattore = getFattore();
    const imuConcordato = Math.round(imuAttuale * fattore * 100) / 100;
    const risparmio = Math.max(imuAttuale - imuConcordato, 0);
    setRisultato({ imu: imuConcordato, risparmio });
  }
  function applySettings() {
    if (!isFinite(aliquotaAttuale) || aliquotaAttuale <= 0) {
      setErrore("Aliquota attuale non valida.");
      return;
    }
    if (!isFinite(aliquotaConcordato) || aliquotaConcordato <= 0) {
      setErrore("Aliquota concordato non valida.");
      return;
    }
    if (!isFinite(abbattimento) || abbattimento <= 0 || abbattimento > 100) {
      setErrore("Abbattimento base non valido (0-100).");
      return;
    }
    setErrore("");
    if (inputValue.trim() !== "") calcola();
  }
  return (
    <div
    //className="imu-widget card shadow-sm"
    >
      <div
      //className="card-body"
      >
        <h2 className="h1 mb-3">
          Calcolo IMU con <em>canone concordato</em>
        </h2>
        <p className="text-muted normal">
          Inserisci quanto <strong>paghi oggi di IMU (annuale)</strong>. Il sistema stimerà quanto pagheresti con il
          <strong> canone concordato</strong> (Comune di Bari).
        </p>
        {/* INPUT */}
        <div className="mb-3">
          <label htmlFor="imu-attuale" className="form-label fw-semibold">
            Quanto paghi oggi di IMU (annuale)?
          </label>
          <div className="input-group">
            <input
              id="imu-attuale"
              type="text"
              className="form-control"
              placeholder="es. 1.780,80"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && calcola()}
            />
            <button className="btn btn-red" onClick={calcola}>
              Calcola Imu con canone concordato
            </button>
          </div>
          <div className="form-text"> Accetta virgola o punto. Esempi validi: 1780.8 — 1.780,80 — 1780,80 </div>
        </div>
        {/* RISULTATO */}
        {risultato && (
          <div className="imu-result alert alert-success mt-3">
            <div className="small text-success mb-1"> IMU stimata con canone concordato: </div>
            <div className="fs-4 fw-bold text-success"> {formatEUR(risultato.imu)} </div>
            <div className="small text-success"> Risparmio stimato: {formatEUR(risultato.risparmio)} all’anno </div>
          </div>
        )}
        {/* ERRORI */}
        {errore && (
          <div className="alert alert-danger mt-3" role="alert">
            {errore}
          </div>
        )}
        {/* DETTAGLI */}
        <details className="mt-3">
          <summary className="fw-semibold">Dettagli e ipotesi di calcolo</summary>
          <div className="mt-2 small text-muted">
            Il calcolo usa le aliquote del Comune di Bari: <strong>1,06%</strong> per l’IMU attuale (libero/sfitto) e
            <strong> 0,40%</strong> per il canone concordato, applicando l’abbattimento della base imponibile al
            <strong> 75%</strong> (art. 1, c. 760, L. 160/2019). <br /> Fattore di conversione = (0,75 × 0,40%) / 1,06% =
            <strong> {getFattore().toFixed(10).replace(".", ",")}</strong>.
          </div>
        </details>
        {/* IMPOSTAZIONI */}
        {isAdministrator && (
          <details className="mt-3">
            <summary className="fw-semibold">Impostazioni (admin)</summary>
            <div className="row g-2 mt-2">
              <div className="col-6">
                <label className="form-label small text-muted">Aliquota attuale (%)</label>
                <input
                  type="number"
                  className="form-control"
                  step="0.01"
                  value={aliquotaAttuale}
                  onChange={(e) => setAliquotaAttuale(Number(e.target.value))}
                />
              </div>
              <div className="col-6">
                <label className="form-label small text-muted">Aliquota concordato (%)</label>
                <input
                  type="number"
                  className="form-control"
                  step="0.01"
                  value={aliquotaConcordato}
                  onChange={(e) => setAliquotaConcordato(Number(e.target.value))}
                />
              </div>
              <div className="col-6">
                <label className="form-label small text-muted">Abbattimento base (%)</label>
                <input
                  type="number"
                  className="form-control"
                  step="0.01"
                  value={abbattimento}
                  onChange={(e) => setAbbattimento(Number(e.target.value))}
                />
              </div>
              <div className="col-6 d-grid mt-1">
                <button className="btn btn-primary fw-semibold mt-4" onClick={applySettings}>
                  Applica impostazioni
                </button>
              </div>
            </div>
            <div className="mt-2 small text-muted">
              Puoi aggiornare aliquote/abbattimento senza modificare il codice. Il fattore verrà ricalcolato e mostrato
              sopra.
            </div>
          </details>
        )}
        {/* ESEMPIO */}
        <details className="mt-3">
          <summary className="fw-semibold">Esempio rapido</summary>
          <div className="mt-2 small text-muted">
            Se oggi paghi <strong>€ 1.780,80</strong>, con canone concordato pagheresti circa <strong>€ 504,00</strong>.
          </div>
        </details>
      </div>
    </div>
  );
}
